import { JSX, PropsWithChildren } from 'react'

import { Button } from '../actions/Button'
import { ResponsiveButtonContainer } from '../global/CommonStyling'
import { BaseModal } from './BaseModal'
import { ModalContent } from './ModalContent'
import { ModalVisualMode } from './ModalTypes'
import { getIcon } from './ModalUtils'

export interface PromptModalProps
  extends Omit<ButtonsProps, 'mode'>,
    Omit<SecondaryButtonProps, 'mode'>,
    PrimaryButtonProps {
  contentLabel?: string
  onClose?: () => unknown
  isOpen: boolean
  header?: string
  headerAdditionalText?: string
  requestId?: string
  mode?: ModalVisualMode
  parentSelector?: Element | null
}

const shouldReverseButtons = (mode: ModalVisualMode, shouldKeepButtonsOrder: boolean): boolean => {
  if (shouldKeepButtonsOrder) {
    return false
  }
  return [ModalVisualMode.Danger, ModalVisualMode.Warn, ModalVisualMode.Help].includes(mode)
}

type PrimaryButtonProps = {
  isLoading?: boolean
  primaryButtonLabel: string
  onPrimaryButtonClick?: () => unknown
  loadingMode?: 'icon' | 'label'
}

const PrimaryButton = (props: PrimaryButtonProps) => (
  <Button
    loading={props.isLoading}
    label={props.primaryButtonLabel}
    onClick={props.onPrimaryButtonClick}
    disabled={!props.onPrimaryButtonClick}
    data-testid="prompt-modal-button"
    aria-disabled={props.isLoading}
    loadingMode={props.loadingMode}
  />
)

type SecondaryButtonProps = {
  onSecondaryButtonClick?: () => unknown
  secondaryButtonLabel: string
  mode: ModalVisualMode
}

const SecondaryButton = (props: SecondaryButtonProps) => (
  <Button
    disabled={!props.onSecondaryButtonClick}
    label={props.secondaryButtonLabel}
    mode={props.mode === ModalVisualMode.Danger ? 'danger' : undefined}
    onClick={props.onSecondaryButtonClick}
    variant="secondary"
  />
)

interface ButtonsProps extends PrimaryButtonProps, SecondaryButtonProps {
  mode: ModalVisualMode
  shouldKeepButtonsOrder?: boolean
}

export const ModalButtons = (props: ButtonsProps) => {
  const { mode, shouldKeepButtonsOrder = false } = props
  const buttons = [
    <SecondaryButton {...props} key="secondaryButton" />,
    <PrimaryButton {...props} key={'primaryButton'} />,
  ]

  return (
    <ResponsiveButtonContainer $hasMarginTop={true}>
      {shouldReverseButtons(mode, shouldKeepButtonsOrder) ? [...buttons].reverse() : buttons}
    </ResponsiveButtonContainer>
  )
}

export const PromptModal = (props: PropsWithChildren<PromptModalProps>): JSX.Element => {
  const {
    contentLabel,
    children,
    onClose,
    isOpen,
    header,
    headerAdditionalText,
    mode = ModalVisualMode.Info,
    shouldKeepButtonsOrder,
    requestId,
    parentSelector,
  } = props

  const icon = getIcon(mode)

  return (
    <BaseModal
      parentSelector={parentSelector}
      contentLabel={contentLabel ?? header ?? 'Prompt modal'}
      onClose={onClose}
      isOpen={isOpen}
      footer={<ModalButtons {...props} mode={mode} shouldKeepButtonsOrder={shouldKeepButtonsOrder} />}
    >
      <ModalContent icon={icon} header={header} headerAdditionalText={headerAdditionalText} requestId={requestId}>
        {children}
      </ModalContent>
    </BaseModal>
  )
}
